import React, { memo } from 'react';
import { withStyles } from '@material-ui/core';
import { ErrorOutline, WarningOutlined } from '@material-ui/icons';
import { EMBARGO_VALIDATION_TYPES } from '../constants';
import { getEmbargoSaveErrorBannerViewModel } from '../viewModel';
import type { EmbargoSaveError } from '../types';

const styles = (theme: any) => ({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '12px 16px',
    margin: '0 0 12px 0',
    width: '100%',
    boxSizing: 'border-box' as const,
    borderRadius: 2,
  },
  errorContainer: {
    background: theme.snackbarErrorBG,
  },
  warningContainer: {
    background: theme.warningBGColor,
  },
  icon: {
    width: 20,
    height: 20,
    fontSize: 20,
    marginRight: 9,
    marginTop: 1,
    flexShrink: 0,
  },
  errorIcon: {
    color: theme.violationColor,
    fill: theme.violationColor,
  },
  warningIcon: {
    color: theme.warningTextColorDispatch,
    fill: theme.warningTextColorDispatch,
  },
  title: {
    fontSize: theme.headerTextFontSize,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  errorTitle: {
    color: theme.violationColor,
  },
  warningTitle: {
    color: theme.warningTextColorDispatch,
  },
  description: {
    color: theme.printTableValue,
    fontSize: theme.textFontSize,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
    marginTop: 2,
  },
  warningDescription: {
    color: theme.warningTextColorDispatch,
  },
});

type EmbargoSaveErrorBannerProps = {
  classes: Record<string, string>;
  saveError: EmbargoSaveError | null;
};

const EmbargoSaveErrorBannerComponent = ({
  classes,
  saveError,
}: EmbargoSaveErrorBannerProps) => {
  const viewModel = getEmbargoSaveErrorBannerViewModel(saveError);
  if (!viewModel) {
    return null;
  }
  const isWarning = viewModel.validationType === EMBARGO_VALIDATION_TYPES.WARNING;
  const Icon = isWarning ? WarningOutlined : ErrorOutline;
  const containerClass = isWarning ? classes.warningContainer : classes.errorContainer;
  const iconClass = isWarning ? classes.warningIcon : classes.errorIcon;
  const titleClass = isWarning ? classes.warningTitle : classes.errorTitle;
  const descriptionClass = isWarning ? classes.warningDescription : '';

  return (
    <div
      className={`${classes.container} ${containerClass}`}
      role="alert"
    >
      <Icon className={`${classes.icon} ${iconClass}`} />
      <div>
        <div className={`${classes.title} ${titleClass}`}>{viewModel.title}</div>
        <div className={`${classes.description} ${descriptionClass}`}>{viewModel.message}</div>
      </div>
    </div>
  );
};

export const EmbargoSaveErrorBanner = withStyles(styles)(
  memo(EmbargoSaveErrorBannerComponent)
);
